import { useMutation } from "@apollo/client";
import Label from "@mui/icons-material/Label";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { useCallback, useState } from "react";

import { useBulkActionContext } from "../FacilitiesBulkActionMenu";
import { ManageFacilityTagsFormDialog } from "./ManageFacilityTagsFormDialog";

export function ManageFacilityTags() {
  const { setDialogState, selectedRows } = useBulkActionContext();

  return (
    <MenuItem
      onClick={() => setDialogState({ action: "manageFacilityTags" })}
      disabled={selectedRows.length === 0}
    >
      <ListItemIcon>
        <Label />
      </ListItemIcon>
      <ListItemText>Manage Tags</ListItemText>
    </MenuItem>
  );
}

export const UPDATE_FACILITY_TAGS_MUTATION = gql(`
  mutation ManageFacilityTags($tenantId: ID!, $facilityUpdates: [FacilityTagUpdate!]!) {
    manageFacilityTags(tenantId: $tenantId, facilityUpdates: $facilityUpdates) {
      id
      tags {
        id
        name
      }
    }
  }
`);

export function ManageFacilityTagsDialog() {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { dialogState, onClose, selectedRows } = useBulkActionContext();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [updateTags, { loading }] = useMutation(UPDATE_FACILITY_TAGS_MUTATION, {
    optimisticResponse: (variables) => {
      const facilityUpdatesArray = Array.isArray(variables.facilityUpdates)
        ? variables.facilityUpdates
        : [variables.facilityUpdates];

      return {
        manageFacilityTags: facilityUpdatesArray.map((facilityUpdate) => {
          const tagNames = facilityUpdate?.tagNames || [];

          return {
            __typename: "Facility" as const,
            id: facilityUpdate.facilityId,
            tags: tagNames
              .sort((a, b) => a.localeCompare(b))
              .map((tagName) => ({
                __typename: "Tag" as const,
                id: `temp-${tagName}`,
                name: tagName,
              })),
          };
        }),
      };
    },
  });

  const close = useCallback(() => {
    setShowConfirmation(false);
    onClose();
  }, [onClose]);

  const confirm = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const submit = useCallback(
    async (facilityUpdates: { facilityId: string; tagNames: string[] }[]) => {
      setShowConfirmation(false);
      try {
        // Update each facility individually with the selected tags
        await updateTags({
          variables: {
            tenantId: tenantId ?? "",
            facilityUpdates,
          },
        });

        const tagCount = facilityUpdates.reduce((acc, update) => acc + update.tagNames.length, 0);
        const facilityCount = selectedRows.length;

        alerts.success(
          `Successfully updated ${pluralize(
            "facility",
            facilityCount
          )} with ${pluralize("tag", tagCount)}`
        );

        close();
      } catch (err) {
        alerts.error("An error occurred while updating the facility tags", err);
      }
    },
    [alerts, close, updateTags, selectedRows, tenantId]
  );

  return (
    <ManageFacilityTagsFormDialog
      selectedRows={selectedRows}
      tenantId={tenantId || ""}
      onClose={close}
      onCloseConfirmation={() => setShowConfirmation(false)}
      confirm={confirm}
      open={dialogState?.action === "manageFacilityTags"}
      onSubmit={submit}
      loading={loading}
      showConfirmation={showConfirmation}
      withConfirmation
    />
  );
}
